import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import QRCode from 'react-qr-code';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { QrCode, Save, Upload, Eye, CheckCircle, XCircle, Clock, Bitcoin, ToggleLeft, ToggleRight, Download } from 'lucide-react';

export default function PaymentConfigPage() {
  const [config, setConfig] = useState({
    upiId: '', upiName: '', bankName: '', accountNumber: '',
    ifscCode: '', accountHolderName: '', instructions: '', qrCodeImage: '',
    usdRate: 83,
    upiEnabled: true,
    bankEnabled: true,
    withdrawalEnabled: true,
    minWithdrawalAmount: 100,
    minInvestmentWithdrawal: 50,
    trc20Address: '', trc20Enabled: false, trc20AutoApprove: false,
    bep20Address: '', bep20Enabled: false, bep20AutoApprove: false,
    erc20Address: '', erc20Enabled: false, erc20AutoApprove: false
  });
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bonusAmount, setBonusAmount] = useState('');
  const qrRef = useRef();

  useEffect(() => {
    axios.get('/api/payment/admin-payment-config').then(r => {
      const d = r.data;
      setConfig(prev => ({
        ...prev, ...d,
        // Ensure boolean fields have explicit defaults (not undefined)
        upiEnabled: d.upiEnabled !== undefined ? d.upiEnabled : true,
        bankEnabled: d.bankEnabled !== undefined ? d.bankEnabled : true,
        trc20Enabled: d.trc20Enabled !== undefined ? d.trc20Enabled : false,
        bep20Enabled: d.bep20Enabled !== undefined ? d.bep20Enabled : false,
        erc20Enabled: d.erc20Enabled !== undefined ? d.erc20Enabled : false,
        withdrawalEnabled: d.withdrawalEnabled !== undefined ? d.withdrawalEnabled : true,
      }));
    }).catch(() => {});
    axios.get('/api/payment/admin/pending-setup-fees').then(r => setPending(r.data.pending || [])).catch(() => {});
  }, []);

  const handleQRUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error('QR image must be under 2MB');
    const reader = new FileReader();
    reader.onloadend = () => setConfig(prev => ({ ...prev, qrCodeImage: reader.result }));
    reader.readAsDataURL(file);
  };

  const generateCryptoQrData = (address, network) => {
    if (!address) return '';
    let prefix = '';
    switch (network) {
      case 'TRC20':
        prefix = 'tron:';
        break;
      case 'BEP20':
        prefix = 'ethereum:';
        break;
      case 'ERC20':
        prefix = 'ethereum:';
        break;
      default:
        prefix = '';
    }
    return `${prefix}${address}`;
  };

  const getCryptoNetworkLabel = (key) => {
    if (key === 'trc20') return 'TRC20';
    if (key === 'bep20') return 'BEP20';
    if (key === 'erc20') return 'ERC20';
    return '';
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      await axios.post('/api/payment/admin-payment-config', config);
      toast.success('Payment config saved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  // Change 6: bonus field on approval
  const handleApprove = async (userId) => {
    try {
      await axios.post(`/api/payment/admin/approve-setup-fee/${userId}`, {
        note: 'Payment verified and approved',
        bonusAmount: parseFloat(bonusAmount) || 0
      });
      toast.success(`User approved!${bonusAmount ? ` Bonus of $${bonusAmount} credited.` : ''}`);
      setPending(p => p.filter(u => u._id !== userId));
      setSelected(null);
      setBonusAmount('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approval failed');
    }
  };

  const handleReject = async (userId) => {
    const note = (() => {
      if (typeof window === 'undefined' || typeof window.prompt !== 'function') return 'Payment not verified';
      try {
        return window.prompt('Reason for rejection (optional):') || 'Payment not verified';
      } catch {
        return 'Payment not verified';
      }
    })();
    try {
      await axios.post(`/api/payment/admin/reject-setup-fee/${userId}`, { note });
      toast.success('Payment rejected. User notified.');
      setPending(p => p.filter(u => u._id !== userId));
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Rejection failed');
    }
  };

  const Toggle = ({ checked, onChange, label }) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <div className="relative" onClick={onChange}>
        <div className={`w-10 h-5 rounded-full transition-colors cursor-pointer ${checked ? 'bg-primary-500' : 'bg-dark-500 border border-white/10'}`}>
          <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
        </div>
      </div>
      {label && <span className="text-gray-300 text-xs">{label}</span>}
    </label>
  );

  const inputClass = "w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500";

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Payment Configuration</h1>
          <p className="text-gray-400 mt-1">Configure UPI, bank details, crypto payment methods and manage approvals</p>
        </div>

        {/* Row 1: UPI & Bank */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* UPI & QR */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <QrCode className="w-5 h-5 text-primary-400" /> UPI & QR Code
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-2">QR Code Image</label>
                <div className="flex gap-3 items-start">
                  {config.qrCodeImage ? (
                    <img src={config.qrCodeImage} alt="QR" className="w-24 h-24 rounded-xl border border-white/10 object-contain bg-white p-1" />
                  ) : (
                    <div className="w-24 h-24 bg-dark-600 border border-dashed border-white/20 rounded-xl flex items-center justify-center">
                      <QrCode className="w-8 h-8 text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <button onClick={() => qrRef.current?.click()}
                      className="flex items-center gap-2 bg-dark-600 border border-white/10 text-gray-300 hover:text-white px-4 py-2 rounded-xl text-sm transition-colors">
                      <Upload className="w-4 h-4" /> Upload QR
                    </button>
                    <input ref={qrRef} type="file" accept="image/*" onChange={handleQRUpload} className="hidden" />
                    <p className="text-gray-600 text-xs mt-1">PNG/JPG, max 2MB</p>
                    {config.qrCodeImage && (
                      <button onClick={() => setConfig(p => ({ ...p, qrCodeImage: '' }))} className="text-red-400 hover:text-red-300 text-xs mt-1">Remove QR</button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between py-2 mb-2 border-b border-white/5">
                <div>
                  <p className="text-white text-sm font-medium">UPI Payment</p>
                  <p className="text-gray-500 text-xs">Show UPI payment option to users</p>
                </div>
                <Toggle checked={config.upiEnabled} onChange={() => setConfig(p => ({ ...p, upiEnabled: !p.upiEnabled }))} label={config.upiEnabled ? 'ON' : 'OFF'} />
              </div>
              {[
                { label: 'UPI ID', key: 'upiId', placeholder: 'business@upi' },
                { label: 'UPI Name', key: 'upiName', placeholder: 'Business Name' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs text-gray-400 mb-1.5">{f.label}</label>
                  <input type="text" value={config[f.key] || ''} onChange={e => setConfig(p => ({ ...p, [f.key]: e.target.value }))}
                    className={inputClass} placeholder={f.placeholder} />
                </div>
              ))}
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Bank Account Details</h3>
              <Toggle checked={config.bankEnabled} onChange={() => setConfig(p => ({ ...p, bankEnabled: !p.bankEnabled }))} label={config.bankEnabled ? 'ON' : 'OFF'} />
            </div>
            <div className="space-y-3">
              {[
                { label: 'Account Holder Name', key: 'accountHolderName', placeholder: 'Full name as per bank' },
                { label: 'Bank Name', key: 'bankName', placeholder: 'e.g. HDFC Bank' },
                { label: 'Account Number', key: 'accountNumber', placeholder: 'Enter account number' },
                { label: 'IFSC Code', key: 'ifscCode', placeholder: 'e.g. HDFC0001234' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs text-gray-400 mb-1.5">{f.label}</label>
                  <input type="text" value={config[f.key] || ''} onChange={e => setConfig(p => ({ ...p, [f.key]: e.target.value }))}
                    className={inputClass} placeholder={f.placeholder} />
                </div>
              ))}
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Payment Instructions</label>
                <textarea value={config.instructions || ''} onChange={e => setConfig(p => ({ ...p, instructions: e.target.value }))}
                  className={`${inputClass} resize-none`} rows={3} placeholder="Instructions shown to users..." />
              </div>
            </div>
          </div>
        </div>

        {/* Change 1: Crypto Payment Networks */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-6 mb-6">
          <h3 className="text-white font-semibold mb-5 flex items-center gap-2">
            <Bitcoin className="w-5 h-5 text-amber-400" /> Crypto Payment Networks
          </h3>
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              { key: 'trc20', label: 'TRC20 (USDT - Tron)', color: 'text-red-400', border: 'border-red-500/20' },
              { key: 'bep20', label: 'BEP20 (BSC - Binance)', color: 'text-amber-400', border: 'border-amber-500/20' },
              { key: 'erc20', label: 'ERC20 (Ethereum)', color: 'text-blue-400', border: 'border-blue-500/20' },
            ].map(({ key, label, color, border }) => (
              <div key={key} className={`bg-dark-600 border ${border} rounded-2xl p-4`}>
                <div className="flex items-center justify-between mb-3">
                  <p className={`font-semibold text-sm ${color}`}>{label}</p>
                  <Toggle
                    checked={config[`${key}Enabled`]}
                    onChange={() => setConfig(p => ({ ...p, [`${key}Enabled`]: !p[`${key}Enabled`] }))}
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Wallet Address</label>
                    <input
                      type="text"
                      value={config[`${key}Address`] || ''}
                      onChange={e => setConfig(p => ({ ...p, [`${key}Address`]: e.target.value }))}
                      className="w-full bg-dark-500 border border-white/10 text-white rounded-xl px-3 py-2.5 text-xs font-mono focus:outline-none focus:border-primary-500"
                      placeholder={`Enter ${key.toUpperCase()} wallet address`}
                    />
                  </div>
                  {config[`${key}Address`] && (
                    <div className="rounded-2xl border border-white/10 bg-dark-700 p-3 mt-3">
                      <p className="text-gray-400 text-xs mb-2">Generated QR for {key.toUpperCase()}</p>
                      <div className="flex flex-col items-center gap-3">
                        <div className="bg-dark-600 rounded-2xl p-3">
                          <QRCode
                            value={generateCryptoQrData(config[`${key}Address`], getCryptoNetworkLabel(key))}
                            size={140}
                            bgColor="#111827"
                            fgColor="#ffffff"
                            includeMargin={true}
                          />
                        </div>
                        <p className="text-gray-400 text-[11px] text-center break-all">{config[`${key}Address`]}</p>
                      </div>
                    </div>
                  )}
                  {config[`${key}Address`] && config[`${key}Enabled`] && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs">Auto-Approve</span>
                      <Toggle
                        checked={config[`${key}AutoApprove`]}
                        onChange={() => setConfig(p => ({ ...p, [`${key}AutoApprove`]: !p[`${key}AutoApprove`] }))}
                        label={config[`${key}AutoApprove`] ? 'On' : 'Off'}
                      />
                    </div>
                  )}
                  {config[`${key}Enabled`] && !config[`${key}Address`] && (
                    <p className="text-amber-400 text-xs">Please add wallet address to enable this network</p>
                  )}
                  <div className="flex items-center gap-1.5 text-xs">
                    <div className={`w-1.5 h-1.5 rounded-full ${config[`${key}Enabled`] && config[`${key}Address`] ? 'bg-primary-500' : 'bg-gray-600'}`}></div>
                    <span className="text-gray-500">{config[`${key}Enabled`] && config[`${key}Address`] ? 'Active — users can pay via this network' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Change 3: Withdrawal Settings */}
          <div className="bg-dark-600 border border-red-500/20 rounded-2xl p-5 mb-4">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <span className="text-red-400">⬇</span> Withdrawal Settings
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <p className="text-sm text-gray-300 mb-1">Withdrawal Requests</p>
                <p className="text-gray-500 text-xs mb-2">If disabled, users will not be able to request withdrawals</p>
                <div className="flex items-center gap-3">
                  <Toggle checked={config.withdrawalEnabled} onChange={() => setConfig(p => ({ ...p, withdrawalEnabled: !p.withdrawalEnabled }))} />
                  <span className={`text-sm font-semibold ${config.withdrawalEnabled ? 'text-primary-400' : 'text-red-400'}`}>
                    {config.withdrawalEnabled ? 'Withdrawals ENABLED' : 'Withdrawals DISABLED'}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Min Withdrawal Amount ($)</label>
                <input type="number" value={config.minWithdrawalAmount}
                  onChange={e => setConfig(p => ({ ...p, minWithdrawalAmount: e.target.value }))}
                  className="w-full bg-dark-500 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                  min="1" placeholder="100" />
                <p className="text-gray-600 text-xs mt-1">Commission wallet withdrawal</p>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Min Investment Withdrawal ($)</label>
                <input type="number" value={config.minInvestmentWithdrawal}
                  onChange={e => setConfig(p => ({ ...p, minInvestmentWithdrawal: e.target.value }))}
                  className="w-full bg-dark-500 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                  min="1" placeholder="50" />
                <p className="text-gray-600 text-xs mt-1">Investment wallet withdrawal</p>
              </div>
            </div>
          </div>

          {/* INR to USD conversion rate */}
          <div className="bg-dark-600 border border-white/10 rounded-xl p-4 mb-2">
            <label className="block text-xs text-gray-400 mb-1.5">INR → USD Conversion Rate</label>
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm">1 USD =</span>
              <input
                type="number"
                value={config.usdRate}
                onChange={e => setConfig(p => ({ ...p, usdRate: e.target.value }))}
                className="w-32 bg-dark-500 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500"
                placeholder="83"
                min="1"
              />
              <span className="text-gray-400 text-sm">INR</span>
            </div>
            <p className="text-gray-600 text-xs mt-1">Used to show INR amount to users paying via UPI/Bank</p>
          </div>
          <button onClick={handleSaveConfig} disabled={loading}
            className="w-full mt-5 bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
            {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              : <><Save className="w-4 h-4" /> Save All Payment Config</>}
          </button>
        </div>

        {/* Pending Setup Fee Approvals */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Pending Setup Fee Approvals
              {pending.length > 0 && (
                <span className="bg-amber-500 text-dark-900 text-xs px-2 py-0.5 rounded-full font-bold">{pending.length}</span>
              )}
            </h3>
          </div>

          {pending.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">No pending approvals</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2">
              {/* User List */}
              <div className="divide-y divide-white/5 border-r border-white/5">
                {pending.map(u => (
                  <div key={u._id} onClick={() => { setSelected(u); setBonusAmount(''); }}
                    className={`px-5 py-4 cursor-pointer hover:bg-white/2 transition-colors ${selected?._id === u._id ? 'bg-primary-500/10 border-l-2 border-l-primary-500' : ''}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-sm flex-shrink-0">
                        {u.name[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{u.name}</p>
                        <p className="text-gray-500 text-xs truncate">{u.email}</p>
                        {u.setupFeeNote && <p className="text-amber-400 text-xs">Method: {u.setupFeeNote}</p>}
                        <p className="text-gray-600 text-xs mt-0.5">
                          {u.setupFeeSubmittedAt ? new Date(u.setupFeeSubmittedAt).toLocaleString('en-IN') : ''}
                        </p>
                      </div>
                      <span className="bg-amber-500/20 text-amber-300 text-xs px-2 py-0.5 rounded-full flex-shrink-0">Pending</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Screenshot Preview + Actions */}
              <div className="p-5">
                {!selected ? (
                  <div className="flex items-center justify-center h-48 text-gray-600">
                    <div className="text-center">
                      <Eye className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm">Click a user to view their payment screenshot</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center text-primary-400 font-bold">
                        {selected.name[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-semibold">{selected.name}</p>
                        <p className="text-gray-400 text-sm">{selected.email}</p>
                      </div>
                    </div>

                    {selected.setupFeeScreenshot ? (
                      <div className="mb-4">
                        <p className="text-gray-400 text-xs mb-2">Payment Screenshot:</p>
                        <img src={selected.setupFeeScreenshot} alt="Payment proof"
                          className="w-full max-h-64 object-contain rounded-xl border border-white/10 bg-dark-600 p-2 cursor-pointer"
                          onClick={() => window.open(selected.setupFeeScreenshot, '_blank')} />
                        <p className="text-gray-600 text-xs mt-1 text-center">Click to view full size</p>
                      </div>
                    ) : (
                      <div className="bg-dark-600 rounded-xl p-8 text-center mb-4">
                        <p className="text-gray-500 text-sm">No screenshot uploaded</p>
                      </div>
                    )}

                    {/* Change 6: Bonus amount input */}
                    <div className="bg-dark-600 border border-primary-500/20 rounded-xl p-3 mb-4">
                      <label className="text-xs text-gray-400 mb-1.5 flex items-center gap-1.5">
                        <Gift className="w-3.5 h-3.5 text-primary-400" /> Bonus Amount (Optional)
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            value={bonusAmount}
                            onChange={e => setBonusAmount(e.target.value)}
                            className="w-full bg-dark-500 border border-white/10 text-white rounded-xl pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-primary-500"
                            placeholder="0.00 — leave empty for no bonus"
                            min="0"
                          />
                        </div>
                      </div>
                      <p className="text-gray-600 text-xs mt-1">Will be credited to user's main wallet on approval</p>
                    </div>

                    <div className="flex gap-3">
                      <button onClick={() => handleApprove(selected._id)}
                        className="flex-1 bg-primary-600 hover:bg-primary-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors">
                        <CheckCircle className="w-4 h-4" /> Approve
                      </button>
                      <button onClick={() => handleReject(selected._id)}
                        className="flex-1 bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors">
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
